import { getToken, clearToken } from "./auth";
import type { OpenAiImageParams } from "./books-api";

const BASE = import.meta.env.VITE_API_BASE ?? "";

/**
 * Globale Einstellungen einzelner Pipeline-Schritte — im Gegensatz zu den
 * buchgebundenen Preview-/Szenen-Settings (books-api.ts).
 *
 * Character-Sheet: das Sheet entsteht aus EINEM globalen Prompt
 * ("character_angle_sheet") und dem eingefrorenen Master-Sheet, einmal pro
 * Bestellposition, für jedes Buch identisch. Seine Modellparameter gehören
 * deshalb neben den Prompt und nicht ans Buch.
 */
export interface CharacterSheetSettings {
  /** Timeout des Sheet-Renders in ms. null = globaler Timeout (min. 300 s). */
  timeoutMs: number | null;
  openai: OpenAiImageParams;
}

/**
 * Serverseitige Defaults, gespiegelt für den Fall, dass die Seite vor der
 * ersten Antwort rendert. GET liefert sie zusätzlich mit, damit der
 * „Defaults"-Knopf auch nach einer Server-Änderung stimmt.
 */
export const DEFAULT_CHARACTER_SHEET_SETTINGS: CharacterSheetSettings = {
  timeoutMs: 300000,
  openai: {
    model: "gpt-image-2.5-sunburst",
    // Quadratisch wie das Master-Sheet: "auto" lässt das Modell ein Verhältnis
    // wählen, das Ansichten aus dem Sheet schneidet.
    size: "1024x1024",
    quality: "max",
    background: "",
    inputFidelity: "",
    outputFormat: "",
    outputCompression: "",
    moderation: "",
    n: "1",
    extra: {},
  },
};

async function authed(path: string, init?: RequestInit): Promise<Response> {
  const token = getToken();
  const headers: Record<string, string> = {
    ...(init?.headers as Record<string, string> | undefined),
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  if (res.status === 401) {
    clearToken();
    window.location.href = "/login";
    throw new Error("unauthorized");
  }
  return res;
}

async function json<T>(path: string, method: string, body?: unknown): Promise<T> {
  const res = await authed(path, {
    method,
    headers: body === undefined ? {} : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`request failed: ${res.status} ${text}`);
  }
  return res.json();
}

export interface CharacterSheetSettingsResponse {
  settings: CharacterSheetSettings;
  defaults: CharacterSheetSettings;
}

export const settingsApi = {
  characterSheet: () =>
    json<CharacterSheetSettingsResponse>("/api/settings/character-sheet", "GET"),
  saveCharacterSheet: (settings: CharacterSheetSettings) =>
    json<CharacterSheetSettingsResponse>("/api/settings/character-sheet", "PUT", settings),
};

/** Pflegbares Template der Versandmail an Kunden (Klartext mit {{platzhaltern}}). */
export interface ShippingMailTemplate {
  subject: string;
  body: string;
}

export interface ShippingMailSettingsResponse {
  template: ShippingMailTemplate;
  defaults: ShippingMailTemplate;
  placeholders: { key: string; description: string }[];
  test_recipient: string;
}

/** Erstversand oder Rücksendung (Paket kam zurück und geht erneut raus). */
export type ShippingMailVariant = "shipping" | "return";

export const shippingMailApi = {
  get: (variant: ShippingMailVariant) =>
    json<ShippingMailSettingsResponse>(`/api/settings/shipping-mail/${variant}`, "GET"),
  save: (variant: ShippingMailVariant, template: ShippingMailTemplate) =>
    json<ShippingMailSettingsResponse>(
      `/api/settings/shipping-mail/${variant}`,
      "PUT",
      template,
    ),
};

export interface RenderedMail {
  subject: string;
  html: string;
  text?: string;
}

export const shippingMailPreviewApi = {
  /** Entwurf mit Beispieldaten rendern (gleicher Renderer wie der echte Versand). */
  preview: (variant: ShippingMailVariant, template: ShippingMailTemplate) =>
    json<RenderedMail>(`/api/settings/shipping-mail/${variant}/preview`, "POST", template),
};

/** Textblock einer Shopify-Versandmail; der Rahmen ist für alle gleich. */
export interface ShopifyMailBlock {
  subject: string;
  titel: string;
  text: string;
  tracking: boolean;
  buttonText: string;
  schluss: string;
}

export type ShopifyMailKey = "versandbestaetigung" | "versand_update" | "wird_zugestellt" | "zugestellt";

export interface ShopifyMail {
  key: ShopifyMailKey;
  name: string;
  /** Wo die Mail in Shopify liegt (Einstellungen → Benachrichtigungen → …). */
  shopifyPath: string;
  file: string;
  block: ShopifyMailBlock;
  defaults: ShopifyMailBlock;
  /** Fertiger Liquid-Code zum Einfügen in Shopify (gespeicherter Stand). */
  liquid: string;
}

export interface ShopifyMailSample {
  vorname: string;
  kinder: string[];
}

export const shopifyMailApi = {
  list: () => json<{ mails: ShopifyMail[] }>("/api/settings/shopify-mails", "GET"),
  save: (key: ShopifyMailKey, block: ShopifyMailBlock) =>
    json<ShopifyMail>(`/api/settings/shopify-mails/${key}`, "PUT", block),
  preview: (key: ShopifyMailKey, block: ShopifyMailBlock, sample: ShopifyMailSample) =>
    json<RenderedMail & { liquid: string }>(
      `/api/settings/shopify-mails/${key}/preview`,
      "POST",
      { block, sample },
    ),
};
